import { ChevronsUpDown, Plus } from "lucide-react"
import { useState } from "react"
import { useT } from "@/i18n"
import { BriefCreateDialog, useGetBriefsQuery } from "@/brief"
import { Button } from "@/ui/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/ui/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/components/ui/popover"
import { Label } from "@/ui/components/ui/label"
import { cn } from "@/ui/lib/utils"
import type { Uuid } from "@/utils"

type Props = {
  readonly value: Uuid | null
  readonly onChange: (value: Uuid | null) => void
  readonly label?: string
}

/**
 * Combobox over the workspace's unarchived Briefs with an inline
 * "create new brief" escape hatch (product.md §10.6)
 */
export default function BriefCombobox({ value, onChange, label }: Props) {
  const { t } = useT("assignment")
  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const { data: briefs } = useGetBriefsQuery(false)

  const selected = briefs?.find((brief) => brief.id === value)

  const select = (briefId: Uuid | null) => {
    onChange(briefId)
    setOpen(false)
  }

  return (
    <div className="flex flex-col gap-2">
      {!!label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button variant="outline" className="justify-between gap-2">
              <span className={cn(!selected && "text-muted-foreground")}>
                {selected?.name ?? t("send.briefPlaceholder")}
              </span>
              <ChevronsUpDown className="text-muted-foreground" />
            </Button>
          }
        />
        <PopoverContent className="p-0" align="start">
          <Command>
            <CommandInput placeholder={t("send.briefSearch")} />
            <CommandList>
              <CommandEmpty>{t("send.briefEmpty")}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  forceMount
                  onSelect={() => {
                    select(null)
                  }}
                >
                  {t("send.noBrief")}
                </CommandItem>
                {(briefs ?? []).map((brief) => (
                  <CommandItem
                    key={brief.id}
                    value={brief.id}
                    keywords={[brief.name]}
                    onSelect={() => {
                      select(brief.id)
                    }}
                  >
                    {brief.name}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  forceMount
                  onSelect={() => {
                    setOpen(false)
                    setCreateOpen(true)
                  }}
                >
                  <Plus />
                  {t("send.createBrief")}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <BriefCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(brief) => {
          onChange(brief.id)
        }}
      />
    </div>
  )
}
